import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Menu, X, ChevronDown } from 'lucide-react'

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [dropdownTimeoutId, setDropdownTimeoutId] = useState(null)
  const [mobileExpandedItems, setMobileExpandedItems] = useState({})
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleDropdownMouseEnter = (itemName) => {
    if (dropdownTimeoutId) {
      clearTimeout(dropdownTimeoutId)
      setDropdownTimeoutId(null)
    }
    setOpenDropdown(itemName)
  }

  const handleDropdownMouseLeave = () => {
    const timeoutId = setTimeout(() => {
      setOpenDropdown(null)
    }, 150)
    setDropdownTimeoutId(timeoutId)
  }

  const toggleMobileExpansion = (itemName) => {
    setMobileExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }))
  }

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { 
      name: 'Services', 
      href: '/services',
      hasDropdown: true,
      subItems: [
        { name: 'Technology Concierge', href: '/services/technology-concierge' },
        { name: 'White Glove Service', href: '/services/white-glove-service' },
        { name: 'Technology Magic', href: '/services/technology-magic' },
        { name: 'Managed Security', href: '/services/managed-security' },
        { name: 'Managed Desktops', href: '/services/managed-desktops' },
        { name: 'Managed Help Desk', href: '/services/managed-help-desk' },
        { name: 'Network Operations', href: '/services/network-operations' },
        { name: 'Development Services', href: '/services/development-services' },
        { name: 'Project Recovery', href: '/services/project-recovery' }
      ]
    },
    { name: 'Contact', href: '/contact' },
  ]

  const isActive = (href) => location.pathname === href
  const isServicesActive = () => location.pathname.startsWith('/services')

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-secondary-900/98 backdrop-blur-md shadow-lg shadow-primary-500/10'
          : 'bg-secondary-900/95 backdrop-blur-sm'
      }`}
    >
      <div className="container-max">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/images/logo/Logo-Praetorian_Works-Square-235_x_235.png" 
              alt="Praetorian Works" 
              className="w-10 h-10 object-contain"
            />
            <div className="hidden sm:block">
              <span className="text-xl font-bold" style={{ color: '#ebd184' }}>Praetorian</span>
              <span className="text-xl font-light ml-1" style={{ color: '#825926' }}>Works</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => handleDropdownMouseEnter(item.name)}
                    onMouseLeave={handleDropdownMouseLeave}
                  >
                    {/* Parent Menu Item */}
                    <Link
                      to={item.href}
                      className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center space-x-1 ${
                        isServicesActive()
                          ? 'text-primary-400'
                          : 'text-primary-500 hover:text-primary-400'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                    </Link>
                    {isServicesActive() && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"
                      />
                    )}
                    
                    {/* Dropdown Menu */}
                    {openDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-1 w-64 bg-secondary-800 rounded-xl shadow-xl border border-primary-500/20 overflow-hidden z-50"
                        onMouseEnter={() => handleDropdownMouseEnter(item.name)}
                        onMouseLeave={handleDropdownMouseLeave}
                      >
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`block px-6 py-3 text-sm transition-colors duration-200 ${
                              isActive(subItem.href)
                                ? 'text-primary-400 bg-secondary-700'
                                : 'text-neutral-300 hover:text-primary-400 hover:bg-secondary-700'
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                      isActive(item.href)
                        ? 'text-primary-400'
                        : 'text-primary-500 hover:text-primary-400'
                    }`}
                  >
                    {item.name}
                    {isActive(item.href) && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"
                      />
                    )}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-primary-500 hover:text-primary-400 transition-colors duration-200"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden py-4 border-t border-primary-500/20"
          >
            <nav className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.hasDropdown ? (
                    <div>
                      <div className="flex items-center justify-between">
                        <Link
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block px-3 py-2 text-base font-medium transition-colors duration-200 flex-1 rounded-lg ${
                            isServicesActive()
                              ? 'text-primary-400 bg-secondary-800'
                              : 'text-primary-500 hover:text-primary-400 hover:bg-secondary-800'
                          }`}
                        >
                          {item.name}
                        </Link>
                        <button
                          onClick={() => toggleMobileExpansion(item.name)}
                          className="p-2 text-primary-500 hover:text-primary-400"
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileExpandedItems[item.name] ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      
                      {mobileExpandedItems[item.name] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="ml-4 mt-2 space-y-2"
                        >
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block px-3 py-2 text-sm transition-colors duration-200 rounded-lg ${
                                isActive(subItem.href)
                                  ? 'text-primary-400 bg-secondary-800'
                                  : 'text-neutral-300 hover:text-primary-400 hover:bg-secondary-800'
                              }`}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-3 py-2 text-base font-medium transition-colors duration-200 rounded-lg ${
                        isActive(item.href)
                          ? 'text-primary-400 bg-secondary-800'
                          : 'text-primary-500 hover:text-primary-400 hover:bg-secondary-800'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
          </motion.div>
        )}
      </div>
    </motion.header>
  )
}

export default Header
